import {
  Skeleton,
  Space,
  Spin,
  Switch,
  Table,
  TableColumnsType,
  TableProps,
  Tag,
} from "antd";
import dayjs from "dayjs";
import React from "react";
import { useLoader } from "../../Context/LoaderContext";
import { TeamMember } from "../../Entities/TeamMember";
import useUser from "../../Hooks/useUser";
import TeamMembersAnalytics from "./TeamMembersAnalytics";
import TeamMembersFilterMenu from "./components/TeamMembersFilterMenu";
import { PaginationType, FindAllTeamMembersFilter } from "../../Interface";
import useAccess from "../../Hooks/useAccess";
import "./index.scss";

interface DataType {
  key: React.Key;
  name: string;
  email_id: string;
  joining_date: string;
  role: string;
  status: "Active" | "Inactive";
  isAvailableForInterview: boolean;
}

interface TeamMembersListProps {
  teamMembers: TeamMember[];
  pagination: PaginationType;
  handlePageChange: (page: number, size?: number) => void;
  handleBlockAndUnblock: any;
  isLoading?: boolean;
  statistics?: {
    activeMembers: number;
    inactiveMembers: number;
    availableForInterview: number;
    notAvailableForInterview: number;
  } | null;
  filters: FindAllTeamMembersFilter;
  updateFilters: (
    key: keyof FindAllTeamMembersFilter,
    value: any | null,
  ) => void;
  onSearch: (query: string) => void;
  onClearAll: () => void;
}

const TeamMembersList: React.FC<TeamMembersListProps> = ({
  teamMembers,
  pagination,
  handlePageChange,
  handleBlockAndUnblock,
  isLoading: propIsLoading,
  statistics,
  filters,
  updateFilters,
  onSearch,
  onClearAll,
}) => {
  const { isLoading: globalIsLoading } = useLoader();
  const isLoading =
    propIsLoading !== undefined ? propIsLoading : globalIsLoading;
  const { user } = useUser();
  const { currentLimits } = useAccess();

  const columns: TableColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      minWidth: 150,
      render: (text) =>
        isLoading ? <Skeleton.Input active size="small" /> : text,
    },
    {
      title: "Email Id",
      dataIndex: "email_id",
      key: "email_id",
      minWidth: 200,
      render: (text) =>
        isLoading ? <Skeleton.Input active size="small" /> : text,
    },
    {
      title: "Joining Date",
      dataIndex: "joining_date",
      key: "joining_date",
      minWidth: 120,
      render: (text) =>
        isLoading ? <Skeleton.Input active size="small" /> : text,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      minWidth: 100,
      render: (text) =>
        isLoading ? <Skeleton.Input active size="small" /> : text,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      minWidth: 100,
      render: (text, record) =>
        isLoading ? (
          <Skeleton.Button active size="small" />
        ) : (
          <Space size="middle">
            {record.status === "Active" && <Tag color="green">Active</Tag>}
            {record.status === "Inactive" && <Tag color="red">Inactive</Tag>}
          </Space>
        ),
    },
    {
      title: "Interview Availability",
      dataIndex: "isAvailableForInterview",
      key: "isAvailableForInterview",
      minWidth: 150,
      render: (isAvailable) =>
        isLoading ? (
          <Skeleton.Button active size="small" />
        ) : (
          <Tag color={isAvailable ? "green" : "red"}>
            {isAvailable ? "Available" : "Unavailable"}
          </Tag>
        ),
    },
  ];

  if (user && user.role === "admin") {
    columns.push({
      title: "Action",
      key: "action",
      minWidth: 100,
      render: (text, record) =>
        isLoading ? (
          <Skeleton.Button active size="small" />
        ) : (
          record.role !== "admin" && (
            <Space size="middle">
              <Switch
                checked={record.status === "Active"}
                onChange={() => handleBlockAndUnblock(record)}
              />
            </Space>
          )
        ),
    });
  }

  const data: DataType[] = isLoading
    ? Array(4)
        .fill({})
        .map((_, index) => ({
          key: `skeleton-${index}`,
          name: "",
          email_id: "",
          joining_date: "",
          role: "",
          status: "Active",
          isAvailableForInterview: false,
        }))
    : teamMembers.map((member) => ({
        key: member._id,
        name: `${member.firstName} ${member.lastName}`,
        email_id: member.email,
        joining_date: dayjs(member.createdAt).format("DD-MMM-YY"),
        role: member.role,
        status: member.isActive ? "Active" : "Inactive",
        isAvailableForInterview: member.isAvailableForInterviews || false,
      }));

  // Calculate total min-width for horizontal scroll
  const totalMinWidth = columns.reduce((total, column) => {
    return total + (column.minWidth || 100);
  }, 0);

  const onChange: TableProps<DataType>["onChange"] = (paginationInfo) => {
    handlePageChange(paginationInfo.current || 1, paginationInfo.pageSize);
  };

  // Use statistics from API instead of local calculations
  const totalMembers = statistics
    ? statistics.activeMembers + statistics.inactiveMembers
    : 0;
  const recruitersLimit = currentLimits?.teamMembers ?? 10;
  const activeCount = statistics?.activeMembers || 0;
  const inactiveCount = statistics?.inactiveMembers || 0;
  const availableCount = statistics?.availableForInterview || 0;
  const unavailableCount = statistics?.notAvailableForInterview || 0;

  return (
    <section className="team-members-listing-section">
      <TeamMembersAnalytics
        totalMembers={totalMembers}
        recruitersLimit={recruitersLimit}
        activeCount={activeCount}
        inactiveCount={inactiveCount}
        availableCount={availableCount}
        unavailableCount={unavailableCount}
        userRole={user?.role}
      />
      <TeamMembersFilterMenu
        filters={filters}
        updateFilters={updateFilters}
        onSearch={onSearch}
        onClearAll={onClearAll}
      />
      <Spin spinning={isLoading}>
        <div
          className="table-container"
          style={{ width: "100%", overflow: "hidden" }}
        >
          <Table
            columns={columns}
            dataSource={!isLoading && data.length === 0 ? [] : data}
            locale={{
              emptyText: isLoading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : (
                "No Data"
              ),
            }}
            size="small"
            scroll={{ x: totalMinWidth, y: 600 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              showSizeChanger: false,
              total: pagination.totalRecords,
              hideOnSinglePage: false,
            }}
            onChange={onChange}
          />
        </div>
      </Spin>
    </section>
  );
};

export default TeamMembersList;
